import { useState } from 'react';
import useFormValidation from './useFormValidation';
import { TranslateFunction } from './useTranslate';
import { TFunction } from 'i18next';

type UseCustomFieldsFormsProps<TFormData, TFieldsConfig> = {
    translate: (key: string) => string | TranslateFunction | TFunction;
    initialValues: TFormData;

    useFieldsConfig: (args: {
        translate: (key: string) => string | TranslateFunction | TFunction;
        formData: TFormData;
        handleBlurValidation: (...args: any[]) => void;
    }) => { fieldsConfig: TFieldsConfig };

    customHandleChange?: (args: {
        name: keyof TFormData;
        value: any;
        setFormData: React.Dispatch<React.SetStateAction<TFormData>>;
        setErrors: React.Dispatch<React.SetStateAction<any>>;
    }) => void;
};

export function useCustomFieldsForms<TFormData extends object, TFieldsConfig>({
    translate,
    initialValues,
    useFieldsConfig,
    customHandleChange
}: UseCustomFieldsFormsProps<TFormData, TFieldsConfig>) {
    const [formData, setFormData] = useState<TFormData>(initialValues);

    const { errors, hasValidationErrors, validateForm, handleBlurValidation, setErrors } =
        useFormValidation();

const { fieldsConfig } = useFieldsConfig({
    translate: translate,
    formData: formData,
    handleBlurValidation: handleBlurValidation
});

    function handleChange(name: keyof TFormData | string, value: any) {
        if (customHandleChange) {
            customHandleChange({ name: name as keyof TFormData, value, setFormData, setErrors });
            return;
        }

        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));

        if (name in formData) {
            setFormData((prevState) => ({
                ...prevState,
                [name as keyof TFormData]: value
            }));
        }
    }

    return {
        formData,
        errors,
        hasValidationErrors,
        fieldsConfig,
        handleChange,
        validateForm
    };
}
